import {testIdProps} from '@github-ui/test-id-props'
import {PulseIcon} from '@primer/octicons-react'
import {Button, Link} from '@primer/react'
import {parseISO} from 'date-fns'
import {useCallback} from 'react'

import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {StatusUpdateActionTypes} from '../../state-providers/status-updates/hooks/use-status-updates-editor-reducer'
import {useStatusUpdates} from '../../state-providers/status-updates/status-updates-context'
import {
  NEW_STATUS_UPDATE_ID,
  useStatusUpdatesEditorState,
} from '../../state-providers/status-updates/status-updates-editor-state-context'
import {StatusUpdatesResources} from '../../strings'
import {NotificationSubscriptionsToggle} from '../notification-subscriptions-toggle'
import styles from './status-updates-header.module.css'

export function StatusUpdatesHeader() {
  const {memexStatusItems, isLoading} = useStatusUpdates()
  const {statusUpdateDraftsDispatch} = useStatusUpdatesEditorState()
  const {hasWritePermissions, isLoggedIn} = ViewerPrivileges()
  const {memex_status_updates_notifications} = useEnabledFeatures()

  const hasUpdates = memexStatusItems.length !== 0

  const handleOnClick = useCallback(() => {
    const [latestStatus] = memexStatusItems

    if (!latestStatus) {
      return statusUpdateDraftsDispatch({
        type: StatusUpdateActionTypes.SET_STATUS_UPDATE,
        payload: {
          id: NEW_STATUS_UPDATE_ID,
          body: '',
          startDate: null,
          targetDate: null,
          status: null,
        },
      })
    }

    const lastStartDate = latestStatus.statusValue.startDate ? parseISO(latestStatus.statusValue.startDate) : null
    const lastTargetDate = latestStatus.statusValue.targetDate ? parseISO(latestStatus.statusValue.targetDate) : null

    statusUpdateDraftsDispatch({
      type: StatusUpdateActionTypes.SET_STATUS_UPDATE,
      payload: {
        id: NEW_STATUS_UPDATE_ID,
        body: '',
        startDate: lastStartDate,
        targetDate: lastTargetDate,
        status: latestStatus.statusValue?.status ?? null,
      },
    })
  }, [memexStatusItems, statusUpdateDraftsDispatch])

  if (hasUpdates || isLoading) {
    return (
      <div {...testIdProps('status-updates-default-state-header')} className={styles.header}>
        <div className={styles.titleSection}>
          <PulseIcon className={styles.icon} />
          <h3 className={styles.title}>{StatusUpdatesResources.title}</h3>
        </div>
        <div className={styles.actions}>
          {memex_status_updates_notifications && isLoggedIn && <NotificationSubscriptionsToggle />}
          {hasWritePermissions && (
            <Button variant="primary" onClick={handleOnClick} {...testIdProps('status-updates-create-button')}>
              {StatusUpdatesResources.addUpdateButtonText}
            </Button>
          )}
        </div>
      </div>
    )
  }

  if (!hasWritePermissions) return null

  return (
    <div {...testIdProps('status-updates-empty-state-header')} className={styles.emptyState}>
      <PulseIcon size="medium" />
      <h3 className={styles.emptyStateTitle}>{StatusUpdatesResources.nullStateTitle}</h3>
      <span className={styles.emptyStateDescription}>{StatusUpdatesResources.description}</span>
      <div className={styles.emptyStateActions}>
        <Button variant="primary" onClick={handleOnClick} {...testIdProps('status-updates-create-button')}>
          {StatusUpdatesResources.addUpdateButtonText}
        </Button>
        <Link
          href={'https://gh.io/status-updates-learn-more'}
          style={{cursor: 'pointer', marginLeft: '8px'}}
          tabIndex={0}
          target="_blank"
          {...testIdProps('status-updates-learn-more-link')}
        >
          {StatusUpdatesResources.learnMoreLinkText}
        </Link>
      </div>
    </div>
  )
}
